import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons/faCircleExclamation';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  AvailabilityStatusService,
  ErrorResponse,
  JobAvailabilityType,
  JobPreference,
  Sitter,
  SitterGeneralAvailabilityService,
  SitterService,
} from 'us-web-services';
import { Alert } from '../Alert/Alert';
import { Button, ButtonType } from '../Button';
import { CheckboxListItem } from '../CheckboxListItem';
import { Storage } from '../constants';
import { defaultAvailability, GeneralAvailability } from '../GeneralAvailability';
import { Modal } from '../Modal';
import { GeneralAvailabilityDay } from '../types';
import { DateFormat, formatDateToString, formatStringToDate, getHourDifference } from '../utils/date';

interface Props {
  onSuccess(): void;
  sitter: Sitter;
}

const DAY_HOURS = 24;

export const GeneralAvailabilityModal = ({ onSuccess, sitter }: Props): JSX.Element => {
  const { t } = useTranslation();

  const [availability, setAvailability] = useState<GeneralAvailabilityDay[]>(defaultAvailability);
  const [availableFullTime, setAvailableFullTime] = useState(sitter.jobPreferences?.some(preference => preference.type === JobAvailabilityType.fullTime) ?? true);
  const [availableOccasional, setAvailableOccasional] = useState(sitter.jobPreferences?.some(preference => preference.type === JobAvailabilityType.occasional) ?? true);
  const [availablePartTime, setAvailablePartTime] = useState(sitter.jobPreferences?.some(preference => preference.type === JobAvailabilityType.partTime) ?? true);
  const [disableSave, setDisableSave] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const closeModal = (): void => {
    localStorage.setItem(Storage.AVAILABILITY_MODAL_LAST_SHOWN, formatDateToString(new Date(), DateFormat.API_DATE_TIME_FORMAT));

    setModalIsOpen(false);
  };

  const getJobPreferences = (): JobPreference[] => {
    const jobPreferences: JobPreference[] = [];

    if (availableFullTime) {
      jobPreferences.push({ type: JobAvailabilityType.fullTime });
    }

    if (availableOccasional) {
      jobPreferences.push({ type: JobAvailabilityType.occasional });
    }

    if (availablePartTime) {
      jobPreferences.push({ type: JobAvailabilityType.partTime });
    }

    return jobPreferences;
  };

  const hasSomeAvailability = (): boolean => availability.some(
    day =>
      day.timeEarly ||
      day.timeMorning ||
      day.timeAfternoon ||
      day.timeEvening ||
      day.timeOvernight,
  );

  const handleGoBack = (): void => {
    setShowConfirmClose(false);
  };

  const handleClose = (): void => {
    setShowConfirmClose(true);
  };

  const saveGeneralAvailability = async (): Promise<void> => {
    setDisableSave(true);

    try {
      const sitterId = sitter.id;

      if (sitter.availabilityStatus?.id) {
        await AvailabilityStatusService.patch(sitter.availabilityStatus.id, {
          id: sitter.availabilityStatus.id,
          jobPreferences: getJobPreferences(),
          sitterId,
        });
      } else {
        await AvailabilityStatusService.create({
          jobPreferences: getJobPreferences(),
          sitterId,
        });
      }

      const availabilityData = {
        availability,
        sitterId,
      };

      await SitterGeneralAvailabilityService.create(availabilityData);

      closeModal();
      onSuccess();
    } catch (e: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      const error: ErrorResponse = e.response?.data;
      const errorString = error?.errors?.[0]?.message || t('components.generalAvailability.saveError');

      setErrorMessage(errorString);
    } finally {
      setDisableSave(false);
    }
  };

  const shouldDisableSave = (): void => {
    const hasSomeJobFrequency = availableFullTime || availableOccasional || availablePartTime;
    const isAvailable = hasSomeAvailability();

    setDisableSave(!(hasSomeJobFrequency && isAvailable));
  };

  const checkAvailability = async (): Promise<void> => {
    const startDate = new Date();
    const start = formatDateToString(startDate, DateFormat.API_DATE_TIME_FORMAT);

    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 90);
    const end = formatDateToString(endDate, DateFormat.API_DATE_TIME_FORMAT)

    const params = {
        sitterId: sitter.id,
        start,
        end,
    };

    const sitterSchedules = (await SitterService.getSchedules({ params })).data;

    if (sitterSchedules.data.length === 0) {
      shouldDisableSave();
      setModalIsOpen(true);
    }
  };

  const showModalIfNotRecentlyShown = (): void => {
    const lastShown = localStorage.getItem(Storage.AVAILABILITY_MODAL_LAST_SHOWN);
    const lastShownDate = lastShown ? formatStringToDate(lastShown) : null;
    const hourDifference = lastShownDate ? getHourDifference(lastShownDate, new Date()) : 0;

    if (!lastShownDate || hourDifference > DAY_HOURS) {
      checkAvailability();
    }
  };

  const toggleAvailableOccasional = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const isEnabled = e.target.checked;

    setAvailableOccasional(isEnabled);
  };

  const toggleAvailablePartTime = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const isEnabled = e.target.checked;

    setAvailablePartTime(isEnabled);
  };

  const toggleAvailableFullTime = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const isEnabled = e.target.checked;

    setAvailableFullTime(isEnabled);
  };

  const updateAvailability = (newAvailability: GeneralAvailabilityDay[]): void => {
    setAvailability(newAvailability);
  };

  useEffect(() => {
    showModalIfNotRecentlyShown();
  }, []);

  useEffect(() => {
    shouldDisableSave();
  }, [availableFullTime, availableOccasional, availablePartTime, availability]);

  return (
    <Modal isOpen={modalIsOpen} className='max-w-[810px] text-base'>
      <Alert
        isOpen={!!errorMessage}
        className='mb-4'
        color='error'
      >
        {errorMessage}
      </Alert>
      {showConfirmClose ? (
        <div className='text-center max-w-[280px]'>
          <FontAwesomeIcon icon={faCircleExclamation} className='text-honey text-[2rem] mb-2' />
          <h2 className='font-semibold mb-4'>
            {t('components.generalAvailability.closeConfirmation.areYouSure')}
          </h2>
          <p className='leading-relaxed text-sm mb-4'>
            {t('components.generalAvailability.closeConfirmation.details')}
          </p>
          <p className='leading-relaxed text-left text-xs font-semibold mb-6'>
            {t('components.generalAvailability.closeConfirmation.subtext')}
          </p>
          <Button buttonType={ButtonType.primary} onClick={handleGoBack} className='mr-6'>
            {t('components.generalAvailability.closeConfirmation.goBack')}
          </Button>
          <Button buttonType={ButtonType.secondary} onClick={closeModal}>
            {t('components.generalAvailability.closeConfirmation.dismiss')}
          </Button>
        </div>
      ) : (
        <>
          <Button
            aria-label='Close modal'
            onClick={handleClose}
            className='absolute right-4 sm:right-6 sm:top-4 px-2 text-xl text-neutral-400 hover:text-neutral-600 transition-colors'
          >
            <FontAwesomeIcon icon={faXmark} />
          </Button>

          <div className='sm:overflow-auto'>
            <div className='sm:w-72 sm:float-left'>
              <h2 className='text-xl sm:text-2xl font-bold mb-3'>
                {`${t('components.generalAvailability.hi')} ${sitter.firstName}`}.
              </h2>
              <h2 className='text-xl sm:text-2xl font-bold mb-3'>
                {t('components.generalAvailability.letsFindJobs')}
              </h2>
              <p className='leading-relaxed sm:mb-8'>
                {t('components.generalAvailability.noUpcomingAvailability')}
              </p>
              <hr className='my-4 border-0 sm:border-t-2' />
              <p className='leading-relaxed mb-4 sm:hidden'>{t('components.generalAvailability.details')}</p>
            </div>
            <div className='sm:inline-block sm:float-right'>
              <h2 className='text-xl sm:text-base font-bold mb-3 sm:mb-2'>
                {t('components.generalAvailability.generallyAvailable')}
              </h2>
              <GeneralAvailability availability={availability} setAvailability={updateAvailability} />
            </div>
            <div className='sm:w-72 sm:float-left'>
              <h2 className='text-xl sm:text-base font-bold mt-4'>
                {t('components.generalAvailability.whatJobs')}
              </h2>
              <p className='leading-relaxed mb-4 text-sm'>
                {t('components.generalAvailability.deselectAny')}
              </p>
              <CheckboxListItem
                name={JobAvailabilityType.occasional.replace(' ', '')}
                selected={availableOccasional}
                toggleSelected={toggleAvailableOccasional}
                text={t('components.generalAvailability.occasional')}
                expandOnDesktop
              />
              <CheckboxListItem
                name={JobAvailabilityType.partTime.replace(' ', '')}
                selected={availablePartTime}
                toggleSelected={toggleAvailablePartTime}
                text={t('components.generalAvailability.partTime')}
                subtext={t('components.generalAvailability.partTimeHours')}
                expandOnDesktop
              />
              <CheckboxListItem
                name={JobAvailabilityType.fullTime.replace(' ', '')}
                selected={availableFullTime}
                toggleSelected={toggleAvailableFullTime}
                text={t('components.generalAvailability.fullTime')}
                subtext={t('components.generalAvailability.fullTimeHours')}
                expandOnDesktop
              />
            </div>
          </div>

          <div className='sm:text-right'>
            <hr className='my-4 sm:hidden' />
            <div className='text-right space-x-4 mt-4'>
              <Button buttonType={ButtonType.secondary} onClick={handleClose}>
                {t('components.generalAvailability.cancel')}
              </Button>
              <Button buttonType={ButtonType.primary} onClick={saveGeneralAvailability} disabled={disableSave}>
                {t('components.generalAvailability.save')}
              </Button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};
